import { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { toast } from '@/components/ui/toaster';
import type { AttributeDefinition, Category } from '@/types/schemas';
import {
  createCategory,
  updateCategory,
  isCodePrefixAvailable,
  deriveCodePrefix,
} from './setupQueries';
import { Plus, Trash2 } from 'lucide-react';

export function CategoryFormDialog({
  open,
  onOpenChange,
  editing,
  onCreated,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  editing: Category | null;
  onCreated?: (categoryId: string) => void;
}) {
  const qc = useQueryClient();
  const [name, setName] = useState('');
  const [codePrefix, setCodePrefix] = useState('');
  const [threshold, setThreshold] = useState(5);
  const [hasExpiry, setHasExpiry] = useState(false);
  const [attrs, setAttrs] = useState<AttributeDefinition[]>([]);

  useEffect(() => {
    if (open) {
      setName(editing?.name ?? '');
      setCodePrefix(editing?.codePrefix ?? '');
      setThreshold(editing?.lowStockThresholdDefault ?? 5);
      setHasExpiry(editing?.hasExpiry ?? false);
      setAttrs(editing?.attributeSchema ?? []);
    }
  }, [open, editing]);

  useEffect(() => {
    if (!editing && name && !codePrefix) {
      setCodePrefix(deriveCodePrefix(name));
    }
  }, [name, editing, codePrefix]);

  const save = useMutation({
    mutationFn: async () => {
      const prefix = codePrefix.trim().toUpperCase();
      if (prefix.length < 2 || prefix.length > 5 || !/^[A-Z]+$/.test(prefix)) {
        throw new Error('Prefix must be 2-5 uppercase letters');
      }
      if (!editing) {
        const available = await isCodePrefixAvailable(prefix);
        if (!available) throw new Error(`Prefix "${prefix}" is already in use`);
        return createCategory({
          name: name.trim(),
          codePrefix: prefix,
          attributeSchema: attrs,
          lowStockThresholdDefault: threshold,
          hasExpiry,
          isActive: true,
        });
      } else {
        if (prefix !== editing.codePrefix) {
          const available = await isCodePrefixAvailable(prefix);
          if (!available) throw new Error(`Prefix "${prefix}" is already in use`);
        }
        await updateCategory(editing.id, {
          name: name.trim(),
          codePrefix: prefix,
          attributeSchema: attrs,
          lowStockThresholdDefault: threshold,
          hasExpiry,
        });
        return editing.id;
      }
    },
    onSuccess: (id) => {
      qc.invalidateQueries({ queryKey: ['inventory-categories'] });
      toast({ title: editing ? 'Category updated' : 'Category created', variant: 'success' });
      if (!editing && id) onCreated?.(id);
      onOpenChange(false);
    },
    onError: (e) =>
      toast({ title: 'Save failed', description: String(e), variant: 'destructive' }),
  });

  function addAttr() {
    setAttrs((a) => [
      ...a,
      { key: `attr_${a.length + 1}`, label: '', type: 'text', required: false },
    ]);
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] w-[calc(100vw-2rem)] max-w-lg overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{editing ? 'Edit category' : 'Create category'}</DialogTitle>
          <DialogDescription>
            Categories define SKU prefixes and optional custom product attributes.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div>
            <Label>Name *</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Plywood" />
          </div>
          <div>
            <Label>Code prefix * (auto-SKU)</Label>
            <Input
              value={codePrefix}
              onChange={(e) => setCodePrefix(e.target.value.toUpperCase())}
              placeholder="PLY"
              maxLength={5}
            />
          </div>
          <div>
            <Label>Low stock threshold default</Label>
            <Input
              type="number"
              min={0}
              value={threshold}
              onChange={(e) => setThreshold(Number(e.target.value))}
            />
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={hasExpiry} onChange={(e) => setHasExpiry(e.target.checked)} />
            Track expiry dates for products in this category
          </label>

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label>Custom attributes</Label>
              <Button type="button" variant="outline" size="sm" onClick={addAttr}>
                <Plus className="h-3.5 w-3.5" /> Add
              </Button>
            </div>
            {attrs.map((a, i) => (
              <div key={i} className="grid grid-cols-2 gap-2 rounded border p-2">
                <Input
                  placeholder="Label"
                  value={a.label}
                  onChange={(e) => {
                    const label = e.target.value;
                    setAttrs((prev) => {
                      const next = [...prev];
                      next[i] = {
                        ...next[i],
                        label,
                        key: label.toLowerCase().replace(/\s+/g, '_').slice(0, 30) || `attr_${i}`,
                      };
                      return next;
                    });
                  }}
                />
                <select
                  className="h-9 rounded-md border px-2 text-sm"
                  value={a.type}
                  onChange={(e) => {
                    const type = e.target.value as AttributeDefinition['type'];
                    setAttrs((prev) => {
                      const next = [...prev];
                      next[i] = { ...next[i], type };
                      return next;
                    });
                  }}
                >
                  <option value="text">Text</option>
                  <option value="number">Number</option>
                  <option value="select">Select</option>
                </select>
                <label className="col-span-2 flex items-center gap-2 text-xs">
                  <input
                    type="checkbox"
                    checked={a.required}
                    onChange={(e) => {
                      setAttrs((prev) => {
                        const next = [...prev];
                        next[i] = { ...next[i], required: e.target.checked };
                        return next;
                      });
                    }}
                  />
                  Required
                </label>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="col-span-2 text-destructive"
                  onClick={() => setAttrs((prev) => prev.filter((_, j) => j !== i))}
                >
                  <Trash2 className="h-3.5 w-3.5" /> Remove attribute
                </Button>
              </div>
            ))}
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button
            variant="gold"
            disabled={!name.trim() || save.isPending}
            onClick={() => save.mutate()}
          >
            {save.isPending ? 'Saving…' : 'Save'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
